import * as React from "react";
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { Toolbar, Typography, IconButton, TextField, InputAdornment } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';

import Skeleton from '@material-ui/lab/Skeleton';

import { Medewerker } from "../../entities/Medewerker";
import { MedewerkerToolbarSelect } from "../molecules/forms/MedewerkerToolbarSelect";
import { TryAgainButton } from "../atoms/buttons/TryAgainButton";

import { useTranslation } from 'react-i18next';
import { IReportData, IPaginatedReportData, isPaginatedReportData } from '../../entities/reports/IReportData';
import { FetchState } from '../../entities/FetchState';

// Row state enum
export enum RowState {
    Normal = 'normal',
    Inactive = 'inactive'
}

// Column specifications
export interface IColumnDefinition<T> {
    title: string;
    align: "left" | "right" | "center";
    renderer: (value: T) => React.ReactNode;
    sorter?: (a: T, b: T) => number;
    searchable?: boolean; // New property to indicate if this column should be searched
}

export interface IData<T> {
    rows: T[] | IReportData<T> | IPaginatedReportData<T>;
    columns: IColumnDefinition<T>[];
}

interface IToolbar {
    title?: string;
    onRefresh?: () => void;
    refreshText?: string;

    onChangeMedewerker?: (medewerker?: Medewerker) => void;
    medewerkerLabel?: string;
    syncMedewerkerWithRedux?: boolean;
    selectedMedewerker?: Medewerker;

    // Search functionality
    onSearch?: (searchTerm: string) => void;
    searchPlaceholder?: string;
    searchValue?: string;
    enableBuiltinSearch?: boolean; // New property to enable built-in search
}

// All the incoming values
interface IProps<T> {
    data: IData<T>;
    toolbar?: IToolbar;
    loading?: boolean;
    hasError?: boolean;
    errorDetails?: string;
    onClickRow?: (row: T) => void;
    getRowState?: (row: T) => RowState; // NEW: tells the table if a row is 'normal' or 'inactive'
    onLoadMore?: () => void; // NEW: callback for loading more data in paginated tables
}

// Outgoing functions
interface IDispatch {

}

type Order = 'asc' | 'desc';
interface IOrderedColumn extends IColumnDefinition<any> {
    key: number;
    order?: Order;
    orderByOrder: number;
}

// Optimized search function with better performance
export function createDefaultTableSearch<T>(originalData: T[], columns: IColumnDefinition<T>[]) {
    // Pre-calculate searchable columns outside of the callback
    const searchableCols = columns.filter(col => col.searchable !== false);
    
    // Return a stable function instead of using useCallback inside
    return (searchTerm: string): T[] => {
        if (!searchTerm || searchTerm.trim() === '') {
            return originalData;
        }
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        
        return originalData.filter(row => {
            return searchableCols.some(column => {
                const cellValue = column.renderer(row);
                // Optimized string conversion
                let searchText: string;
                
                if (typeof cellValue === 'string') {
                    searchText = cellValue;
                } else if (typeof cellValue === 'number') {
                    searchText = cellValue.toString();
                } else if (React.isValidElement(cellValue)) {
                    return false; // Skip React elements for performance
                } else {
                    searchText = String(cellValue || '');
                }
                
                return searchText.toLowerCase().includes(lowerSearchTerm);
            });
        });
    };
}

// Custom hook for debounced search
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// CSS
const useStyles = makeStyles((theme: Theme) => ({
    tableDataRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },

    },
    // NEW: inactive row styling
    inactiveRow: {
        opacity: 0.6,
        backgroundColor: theme.palette.action.disabledBackground,
        '&:hover': {
            backgroundColor: theme.palette.action.disabledBackground, // Prevent hover effect on inactive rows
        },
    },
    tableDataCell: {
        minHeight: '36px'
    },
    toolbar: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        paddingBottom: theme.spacing(0),
        height: theme.spacing(4),
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.light,
        display: 'flex',
        flexDirection: 'row',
    },
    header: {
        backgroundColor: theme.palette.primary.light,
    },
    headerCell: {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.light,
    },
    toolbarCell: {
        padding: 0,
        borderBottom: 'none',
    },
    title: {
        flex: '1 1 100%',
    },
    iconbutton: {},
    tableSortLabel: {
        color: theme.palette.primary.contrastText, // White color for the label

        '& .MuiTableSortLabel-root': {
            color: theme.palette.primary.contrastText, // Keep white on hover
        },
        '&.MuiTableSortLabel-active': {
            color: theme.palette.primary.contrastText, // Keep white when active
            '&:hover': {
                color: theme.palette.primary.dark,
            },
        },
        '& .MuiTableSortLabel-icon': {
            color: theme.palette.primary.contrastText, // White color for the sort icon
        },
    },
    tableHeaderInputBoxholder: {
        display: 'flex',
        flexDirection: "row",
        justifyContent: 'space-between',
        alignItems: 'center',

        
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    tableHeaderInputBoxItem: {
        width: 300,
        height: theme.spacing(2),
        border: "1px solid red"
    },
    searchField: {
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(1),
        minWidth: 200,
        width: 'auto',
        '& .MuiInputBase-root': {
            color: theme.palette.primary.contrastText,
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.primary.contrastText,
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: theme.palette.primary.contrastText,
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.contrastText,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.contrastText,
            },
        },
    },
    loadingMoreRow: {
        textAlign: 'center',
        padding: theme.spacing(2),
    },
}));

// Actual Component
export type AllProps<T> = IProps<T> & IDispatch;

export const DopTable = <T,>(props: AllProps<T>) => {
    const classes = useStyles(useTheme());
    const { t: tCommon } = useTranslation('common');

    // Detect if rows is paginated data
    const isPaginated = React.useMemo(() => 
        !Array.isArray(props.data.rows) && isPaginatedReportData(props.data.rows as any),
        [props.data.rows]
    );

    // Extract pagination metadata if available
    const paginationData = React.useMemo(() => 
        isPaginated ? (props.data.rows as IPaginatedReportData<T>) : null,
        [isPaginated, props.data.rows]
    );

    // Extract actual rows array
    const actualRows = React.useMemo(() => {
        if (Array.isArray(props.data.rows)) {
            return props.data.rows;
        } else if ('data' in props.data.rows) {
            return (props.data.rows as IReportData<T>).data;
        }
        return [];
    }, [props.data.rows]);

    const [orderedColumns, setOrderedColumns] = React.useState<IOrderedColumn[]>(props.data.columns.map((column, index) => ({
        ...column,
        key: index,
        order: undefined,
        orderByOrder: index,
    })));

    // Memoized sort function
    const calculateSort = React.useCallback((data: T[], orderBy: IOrderedColumn[]) => {
        const sortedData = [...data];
        orderBy.filter(c => c.sorter).sort((a, b) => b.orderByOrder - a.orderByOrder).forEach((column) => {
            if (column.order === 'asc') {
                sortedData.sort((a, b) => {
                    return column.sorter 
                        ? column.sorter(a, b)
                        : a > b ? 1 : -1;
                });
            } else if (column.order === 'desc') {
                sortedData.sort((a, b) => {
                    return column.sorter 
                    ? column.sorter(b, a) 
                    : a < b ? 1 : -1;
                });
            }
        });
        return sortedData;
    }, []);

    const [originalData, setOriginalData] = React.useState<T[]>(actualRows);
    const [searchTerm, setSearchTerm] = React.useState<string>(props.toolbar?.searchValue || '');
    
    // Debounce search term to reduce calculations
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Update originalData when actualRows changes
    React.useEffect(() => {
        setOriginalData(actualRows);
    }, [actualRows]);

    // Memoized search function - use stable dependencies
    const defaultSearch = React.useMemo(() => 
        createDefaultTableSearch(originalData, props.data.columns), 
        [originalData, props.data.columns]
    );

    // Memoized filtered data calculation - ensure stable dependencies
    const filteredData = React.useMemo(() => {
        if (!props.toolbar?.enableBuiltinSearch) {
            return originalData;
        }
        
        return debouncedSearchTerm.trim() 
            ? defaultSearch(debouncedSearchTerm) 
            : originalData;
    }, [originalData, debouncedSearchTerm, defaultSearch, props.toolbar?.enableBuiltinSearch]);

    // Memoized sorted data calculation
    const sortedData = React.useMemo(() => {
        return calculateSort(filteredData, orderedColumns);
    }, [filteredData, orderedColumns, calculateSort]);

    // Handle external search functionality with stable dependencies
    React.useEffect(() => {
        // Only run if we have a search function and a valid search term state
        const hasExternalSearch = props.toolbar?.onSearch && typeof props.toolbar.onSearch === 'function';
        
        if (hasExternalSearch && props.toolbar?.onSearch) {
            if (debouncedSearchTerm.trim().length >= 3) {
                props.toolbar.onSearch(debouncedSearchTerm);
            } else if (debouncedSearchTerm.trim().length === 0) {
                props.toolbar.onSearch('');
            }
        }
    }, [debouncedSearchTerm, props.toolbar?.onSearch]);

    // Determine loading and error states from pagination if available
    const loading = props.loading || (paginationData?.state === FetchState.Busy && paginationData.data.length === 0);
    const error = props.hasError || (paginationData?.state === FetchState.Error);
    const contentReady = !loading && !error;
    const isLoadingMore = paginationData?.loadingMoreState === FetchState.Busy;

    const handleClickSort = (column: IOrderedColumn) => {
        const orderByIndex = orderedColumns.findIndex((col) => col.key === column.key);
        const newOrderByOrder = Math.max(...orderedColumns.map((col) => col.orderByOrder)) + 1;
        const newOrderBy = [...orderedColumns];
        if (orderByIndex !== -1) {
            const currentOrder = newOrderBy[orderByIndex].order;
            newOrderBy[orderByIndex].order = currentOrder === 'asc' ? 'desc' : currentOrder === 'desc' ? undefined : 'asc';
            newOrderBy[orderByIndex].orderByOrder = newOrderByOrder;
        }
        setOrderedColumns(newOrderBy);
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    // Show search field if onSearch is provided OR if built-in search is enabled
    const showSearchField = props.toolbar?.onSearch || props.toolbar?.enableBuiltinSearch;

    return (<>
    <TableContainer component={Paper}>
        <Table size="small" aria-label="Data Table">
        <TableHead>

    {props.toolbar && <TableRow className={classes.header}><TableCell className={classes.toolbarCell} colSpan={props.data.columns.length}><Toolbar className={classes.toolbar}>

        <Typography variant="h6" id="tableTitle" component="div" className={classes.title}>
            {props.toolbar?.title}
        </Typography>

        {showSearchField && (
            <TextField
                className={classes.searchField}
                size="small"
                variant="outlined"
                placeholder={props.toolbar.searchPlaceholder || tCommon('search')}
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon style={{ color: 'inherit' }} />
                        </InputAdornment>
                    ),
                }}
            />
        )}

        {props.toolbar?.onChangeMedewerker &&
            <MedewerkerToolbarSelect 
                selectedMedewerker={props.toolbar.selectedMedewerker}
                syncWithRedux={props.toolbar.syncMedewerkerWithRedux}
                label={props.toolbar.medewerkerLabel}
                onChange={props.toolbar.onChangeMedewerker}
             /> 
        }

        {props.toolbar?.onRefresh && 
            <IconButton className={classes.iconbutton} disabled={loading} onClick={props.toolbar.onRefresh} color="inherit"><Tooltip title={props.toolbar.refreshText ? props.toolbar.refreshText : tCommon('refresh')}><RefreshIcon /></Tooltip></IconButton>
        }

    </Toolbar></TableCell></TableRow> }

                        <TableRow className={classes.header}>
                            {orderedColumns.map((column, index) => (
                                <TableCell key={index} align={column.align}>
                                    <TableSortLabel 
                                        className={classes.tableSortLabel}
                                        disabled={!column.sorter} 
                                        hideSortIcon={!column.sorter} 
                                        onClick={() => handleClickSort(column)} 
                                        direction={column.order}
                                        active={column.sorter && column.order !== undefined}
                                    >
                                        {column.title}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading && 
                            [...Array(5)].map((_, index) => (
                            <TableRow key={index}>
                                {props.data.columns.map((_, columnIndex) => (
                                    <TableCell key={columnIndex}><Skeleton variant="text" /></TableCell>
                                ))}
                            </TableRow>
                        ))}

                        {error && 
                            <TableRow>
                                <TableCell colSpan={props.data.columns.length}>
                                    <TryAgainButton onClick={props.toolbar?.onRefresh || (() => {})} />
                                </TableCell>
                            </TableRow>
                        }

                        {contentReady && sortedData.map((row, index) => {
                            const rowState = props.getRowState ? props.getRowState(row) : RowState.Normal;
                            const isInactive = rowState === RowState.Inactive;
                            const clickable = !!props.onClickRow && !isInactive;

                            return (
                                <TableRow 
                                    key={index} 
                                    hover={clickable}
                                    onClick={clickable ? () => props.onClickRow!(row) : undefined} 
                                    className={`${classes.tableDataRow} ${isInactive ? classes.inactiveRow : ''}`}
                                    style={clickable ? { cursor: 'pointer' } : undefined}
                                    data-row-state={rowState}
                                >
                                    {orderedColumns.map((column, columnIndex) => (
                                        <TableCell key={columnIndex} align={column.align}>
                                            {column.renderer(row)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}

                        {/* Load More button */}
                        {isPaginated && paginationData && contentReady && paginationData.hasMore && props.onLoadMore && (
                            <TableRow>
                                <TableCell colSpan={props.data.columns.length} className={classes.loadingMoreRow}>
                                    {isLoadingMore ? (
                                        <Skeleton variant="text" width="100%" />
                                    ) : (
                                        <IconButton 
                                            onClick={props.onLoadMore}
                                            disabled={isLoadingMore}
                                            color="primary"
                                        >
                                            <Typography variant="button" style={{ marginRight: '8px' }}>
                                                {tCommon('load_more', 'Load More')}
                                            </Typography>
                                            <RefreshIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        )}

                        {/* End of results indicator */}
                        {isPaginated && paginationData && contentReady && !paginationData.hasMore && sortedData.length > 0 && (
                            <TableRow>
                                <TableCell colSpan={props.data.columns.length} className={classes.loadingMoreRow}>
                                    <Typography variant="caption" color="textSecondary">
                                        {tCommon('no_more_results', 'No more results')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
    </>)
}

// Legacy exports for backward compatibility - now deprecated
export interface IMedewerkerStateProps {
    medewerkers: any;
    selectedMedewerker: Medewerker;
}
export interface IMedewerkerStateDispatch {
    fetchMedewerkers: () => void;
    setSelectedMedewerker: (medewerker: Medewerker) => void;
}

export const mappings = {
    medewerkerState: {
        // This is now deprecated - components manage their own state
        alleMedewerkers: {
            state: () => ({}),
            dispatch: () => ({})
        }
    }
}
